import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatHm, type OvertimeBalance } from '../overtime/types';

/**
 * Panel de RRHH en la ficha del empleado: muestra el saldo de "horas a
 * compensar" disponible y permite ajustarlo (sumar o restar) con un
 * POST /overtime/adjustments (minutos con signo).
 */
export function OvertimeBalancePanel({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [adjusting, setAdjusting] = useState(false);

  const balance = useQuery({
    queryKey: ['admin', 'users', userId, 'overtime-balance'],
    queryFn: () => api<OvertimeBalance>(`/overtime/balance/${userId}`),
    enabled: Boolean(userId),
  });

  const data = balance.data;

  return (
    <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
      <header className="flex items-start justify-between gap-4 px-7 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-semibold tracking-tightish">Horas a compensar</h2>
          <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
            Bolsa de horas a compensar acumulada por el empleado. "Ajustar" permite sumar o restar
            horas manualmente por motivos puntuales.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="flex-shrink-0"
          onClick={() => setAdjusting(true)}
          disabled={!data}
          aria-label="Ajustar horas a compensar"
        >
          <Pencil className="h-3.5 w-3.5" />
          Ajustar
        </Button>
      </header>

      {balance.isLoading ? (
        <p className="px-7 py-6 text-sm text-muted-foreground">Cargando…</p>
      ) : balance.isError ? (
        <p role="alert" className="px-7 py-6 text-sm text-destructive">
          {balance.error instanceof ApiError ? balance.error.message : 'Error al cargar el saldo'}
        </p>
      ) : data ? (
        <div className="flex items-center gap-3 px-7 py-4">
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold text-foreground leading-tight">
              Saldo disponible
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
              {formatDaysAndHours(data)} · total {formatSignedHm(data.availableMinutes)}
            </p>
          </div>
          <p
            className={cn(
              'font-mono text-[18px] font-medium tabular-nums tracking-tight2',
              data.availableMinutes < 0 ? 'text-destructive' : 'text-foreground',
            )}
          >
            {formatSignedHm(data.availableMinutes)}
          </p>
        </div>
      ) : null}

      {adjusting && data ? (
        <AdjustOvertimeModal
          userId={userId}
          balance={data}
          onClose={() => setAdjusting(false)}
          onSaved={() => {
            setAdjusting(false);
            queryClient.invalidateQueries({
              queryKey: ['admin', 'users', userId, 'overtime-balance'],
            });
            queryClient.invalidateQueries({ queryKey: ['overtime'] });
          }}
        />
      ) : null}
    </section>
  );
}

function AdjustOvertimeModal({
  userId,
  balance,
  onClose,
  onSaved,
}: {
  userId: string;
  balance: OvertimeBalance;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const minutes = Math.round(parseFloat(hours.replace(',', '.')) * 60);
      return api<unknown>('/overtime/adjustments', {
        method: 'POST',
        body: JSON.stringify({
          userId,
          minutes,
          effectiveDate: today,
          reason: reason.trim(),
        }),
      });
    },
    onSuccess: onSaved,
  });

  const hoursValue = parseFloat(hours.replace(',', '.'));
  const minutes = Number.isFinite(hoursValue) ? Math.round(hoursValue * 60) : NaN;
  const isValid =
    hours !== '' &&
    Number.isFinite(hoursValue) &&
    minutes !== 0 &&
    hoursValue >= -24 &&
    hoursValue <= 24 &&
    reason.trim().length >= 3 &&
    !mutation.isPending;

  const newMinutes = Number.isFinite(minutes)
    ? balance.availableMinutes + minutes
    : balance.availableMinutes;

  const error = mutation.error
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Error al guardar el ajuste'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[480px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">Ajustar horas a compensar</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) mutation.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <div className="rounded-sm border border-border bg-paper px-4 py-3 text-[13px]">
              <p>
                Saldo actual:{' '}
                <span className="font-mono font-semibold tabular-nums">
                  {formatSignedHm(balance.availableMinutes)}
                </span>
              </p>
              {hours !== '' && Number.isFinite(minutes) && minutes !== 0 ? (
                <p className="text-[12px] text-muted-foreground mt-1">
                  Saldo después del ajuste:{' '}
                  <span
                    className={cn(
                      'font-mono font-semibold tabular-nums',
                      newMinutes < 0 && 'text-destructive',
                    )}
                  >
                    {formatSignedHm(newMinutes)}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-adj-hours">
                Horas a aplicar <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ot-adj-hours"
                inputMode="decimal"
                type="number"
                step="0.25"
                min={-24}
                max={24}
                required
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="Ej. 2 o -1.5"
              />
              <p className="text-[11px] text-muted-foreground">
                Positivo suma horas a la bolsa, negativo las resta. Permite cuartos de hora (paso
                0.25).
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-adj-reason">
                Motivo <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="ot-adj-reason"
                rows={3}
                required
                minLength={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej. Compensación cierre de proyecto, corrección de saldo…"
              />
            </div>

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!isValid}>
              {mutation.isPending ? 'Aplicando…' : 'Aplicar ajuste'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/** "1 d 2h30" según los minutos de una jornada; "0h" si no hay saldo positivo. */
function formatDaysAndHours(balance: OvertimeBalance): string {
  const { availableDays, availableRemainderMinutes } = balance;
  if (availableDays <= 0 && availableRemainderMinutes <= 0) {
    return balance.availableMinutes < 0 ? 'sin saldo (deuda)' : '0h';
  }
  if (availableDays === 0) return formatHm(availableRemainderMinutes);
  if (availableRemainderMinutes === 0) return `${availableDays} d`;
  return `${availableDays} d ${formatHm(availableRemainderMinutes)}`;
}

/** formatHm con signo, aceptando minutos negativos (deuda). */
function formatSignedHm(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes)) return '—';
  if (totalMinutes < 0) return `−${formatHm(-totalMinutes)}`;
  return formatHm(totalMinutes);
}
